import { useEffect } from "react";
import {
  motion,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { scrollToId } from "../utils/scroll";

const LINES = ["Masterful", "Custom", "Renovations."];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.45 + i * 0.18, duration: 1.2, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero({ progress }) {
  const opacity = useTransform(progress, [0, 0.09, 0.15], [1, 1, 0]);
  const y = useTransform(progress, [0, 0.15], ["0%", "-40%"]);
  const cueOpacity = useTransform(progress, [0, 0.04], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 22 });
  const sy = useSpring(my, { stiffness: 45, damping: 22 });

  useEffect(() => {
    const handler = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 16);
      my.set((e.clientY / window.innerHeight - 0.5) * 10);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [mx, my]);

  return (
    <motion.div
      style={{ opacity }}
      className="pointer-events-none absolute inset-0 z-20 flex items-end"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y, x: sx }}
        className="mx-auto w-full max-w-[1600px] px-6 pb-24 md:px-10 md:pb-32"
      >
        <motion.div style={{ y: sy }}>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mb-6 text-[0.6rem] font-semibold uppercase tracking-[0.45em] text-gold md:text-xs"
            data-testid="hero-eyebrow"
          >
            Aspen Design — Ottawa · Est. 15+ Years
          </motion.p>

          <h1
            className="font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className="block will-change-transform"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.15, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 h-px w-40 origin-left bg-gold/70"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-sm font-light leading-relaxed text-slate-300 md:text-base"
            data-testid="hero-subheadline"
          >
            15+ years of uncompromising craftsmanship. Elevating residential and
            commercial spaces across Ottawa, Gatineau, Kingston, Cornwall, and
            surrounding areas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={() => scrollToId("#portfolio")}
              data-testid="hero-cta-portfolio"
              className="group relative overflow-hidden border border-gold/60 px-9 py-4"
            >
              <span className="relative z-10 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-500 group-hover:text-navy-900">
                Step Inside Our Work
              </span>
              <span className="absolute inset-0 origin-left scale-x-0 bg-gold transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100" />
            </button>
            <button
              onClick={() => scrollToId("#contact")}
              data-testid="hero-cta-consultation"
              className="border border-white/20 bg-white/5 px-9 py-4 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-white backdrop-blur-md transition-colors duration-500 hover:border-white/40 hover:bg-white/15"
            >
              Request a Consultation
            </button>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: cueOpacity }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[0.55rem] font-semibold uppercase tracking-[0.5em] text-slate-400">
          Scroll
        </span>
        <div className="relative h-14 w-px overflow-hidden bg-white/15">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-0 top-0 h-1/2 w-px bg-gold"
          />
        </div>
      </motion.div>
    </motion.div>
  );
}
